<template>
  <div class="policy-admin-page">
    <el-page-header content="생일반차 정책 관리" />

    <el-card v-loading="loading" style="margin-top: 16px; max-width: 720px;">
      <el-form :model="form" label-width="200px">
        <el-form-item label="제도 활성">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        <el-form-item label="생일 전 사용 가능 일수">
          <el-input-number v-model="form.days_before" :min="0" :max="30" />
        </el-form-item>
        <el-form-item label="생일 후 사용 가능 일수">
          <el-input-number v-model="form.days_after" :min="0" :max="30" />
        </el-form-item>
        <el-form-item label="부여 일수">
          <el-input-number v-model="form.accrual_days" :min="0.5" :max="2" :step="0.5" />
          <span style="margin-left:8px; color:#909399;">반차=0.5, 종일=1.0</span>
        </el-form-item>
        <el-form-item label="윤년 2/29 평년 처리">
          <el-radio-group v-model="form.leap_29_fallback">
            <el-radio value="feb28">2월 28일</el-radio>
            <el-radio value="mar01">3월 1일</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="만료 시 미승인 신청 자동 반려">
          <el-switch v-model="form.auto_reject_expired" />
        </el-form-item>
        <el-form-item label="본인 생일 자동 변경 허용 횟수">
          <el-input-number v-model="form.allow_self_change_count" :min="0" :max="5" />
          <span style="margin-left:8px; color:#909399;">초과 시 HR 승인 필요</span>
        </el-form-item>

        <el-divider>알림</el-divider>
        <el-form-item label="부여 알림">
          <el-switch v-model="form.grant_alert_enabled" />
        </el-form-item>
        <el-form-item label="만료 알림">
          <el-switch v-model="form.expire_alert_enabled" />
        </el-form-item>
        <el-form-item label="D-2 리마인더">
          <el-switch v-model="form.d2_alert_enabled" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="save">저장</el-button>
        </el-form-item>
      </el-form>

      <el-alert type="info" :closable="false" show-icon>
        <template #title>
          정책 변경은 다음 부여부터 적용됩니다.
          이미 부여된 생일반차는 부여 시점의 정책(snapshot)으로 보호되어 변경되지 않습니다.
        </template>
      </el-alert>
    </el-card>

    <el-card v-loading="grantsLoading" style="margin-top:16px;">
      <template #header>최근 부여 현황</template>
      <el-table :data="grants" stripe>
        <el-table-column prop="employee_name" label="직원" width="120"/>
        <el-table-column prop="grant_year" label="연도" width="80"/>
        <el-table-column prop="birthday" label="생일" width="120"/>
        <el-table-column prop="valid_from" label="시작" width="120"/>
        <el-table-column prop="valid_to" label="종료" width="120"/>
        <el-table-column prop="days" label="일수" width="80"/>
        <el-table-column prop="status" label="상태" width="100">
          <template #default="{ row }">
            <el-tag :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="" width="100">
          <template #default="{ row }">
            <el-button v-if="['GRANTED','REQUESTED'].includes(row.status)"
              size="small" type="danger" plain @click="cancelGrant(row)">취소</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'

const loading = ref(false)
const grantsLoading = ref(false)
const form = ref({})
const grants = ref([])

async function fetchPolicy() {
  loading.value = true
  try {
    const r = await axiosInstance.get('birthday-policy/')
    form.value = r.data
  } finally {
    loading.value = false
  }
}

async function fetchGrants() {
  grantsLoading.value = true
  try {
    const r = await axiosInstance.get('birthday-grants/')
    grants.value = r.data.results || r.data
  } finally {
    grantsLoading.value = false
  }
}

async function save() {
  loading.value = true
  try {
    await axiosInstance.patch('birthday-policy/', form.value)
    ElMessage.success('정책이 저장되었습니다')
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || '저장 실패')
  } finally {
    loading.value = false
  }
}

async function cancelGrant(row) {
  try {
    const { value } = await ElMessageBox.prompt('취소 사유를 입력하세요', '생일반차 취소', {
      inputPlaceholder: '예: 퇴사 / 오부여 등',
    })
    await axiosInstance.post(`birthday-grants/${row.id}/cancel/`, { reason: value })
    ElMessage.success('취소되었습니다')
    fetchGrants()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.response?.data?.detail || '취소 실패')
  }
}

function statusType(s) {
  return { GRANTED: 'success', REQUESTED: 'primary', USED: 'info', EXPIRED: 'danger', CANCELLED: 'warning' }[s] || 'info'
}
function statusLabel(s) {
  return { GRANTED: '부여됨', REQUESTED: '신청대기', USED: '사용완료', EXPIRED: '만료', CANCELLED: '취소' }[s] || s
}

onMounted(() => {
  fetchPolicy()
  fetchGrants()
})
</script>

<style scoped>
.policy-admin-page { padding: 16px; }
</style>
